import {CSSProperties, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {decodeToken} from 'react-jwt'
import {toast} from 'react-toastify'
import {HistoryContext} from '../../contexts/history'
import {caronaApi} from '../../services/CaronaApi.service'
import {CookiesService} from '../../services/Cookie.service'
import BuscaForm, {BuscaFormValues} from './components/BuscaForm'
import ResultadosList from './components/ResultadosList'
import DetalheAta from './components/DetalheAta'
import Entrevista from './components/Entrevista'
import JustificativaView from './components/JustificativaView'
import HistoricoView from './components/HistoricoView'
import DicasLoading from './components/DicasLoading'
import {COLORS, ESFERA_LABEL} from './ui'
import {
    AtaDetalhe,
    AtaResumo,
    CriarJustificativaRequest,
    EsferaCodigo,
    EvidenciaIn,
    IrpResumo,
    JustificativaOut,
    JustificativaResumo,
    RespostasEntrevista,
    SugestaoDemandaOut,
} from './types'

type View = 'busca' | 'resultados' | 'detalhe' | 'entrevista' | 'justificativa' | 'historico'

// Poll de reconciliação da sugestão de demanda (rede de segurança quando o WS não
// entrega): intervalo entre tentativas e teto total antes de desistir.
const SUGESTAO_POLL_INTERVAL_MS = 5000
const SUGESTAO_POLL_TIMEOUT_MS = 60000

function defaultRespostas(esfera: EsferaCodigo): RespostasEntrevista {
    return {
        esfera_aderente: esfera,
        demanda_descricao: '',
        quantidades_desejadas: {},
        demanda_prevista_pca: false,
        alega_urgencia: false,
        declara_preco_compativel: false,
        declara_edital_preve_adesao: false,
        ciente_anuencias: false,
        ciente_edital: false,
    }
}

export default function CaronaManager() {
    // Navegação com PILHA (histórico real), em estado atômico {view, pilha}: "Voltar"
    // desempilha p/ a tela ANTERIOR de verdade — não para uma fixa. (Antes a entrevista
    // sempre voltava p/ o detalhe, mesmo vindo da lista de resultados.)
    const [nav, setNav] = useState<{view: View; pilha: View[]}>({view: 'busca', pilha: []})
    const view = nav.view
    const navegar = (v: View) => setNav((n) => ({view: v, pilha: [...n.pilha, n.view]}))
    const voltar = () => setNav((n) => n.pilha.length
        ? {view: n.pilha[n.pilha.length - 1], pilha: n.pilha.slice(0, -1)}
        : {view: 'busca', pilha: []})
    const irParaBusca = () => setNav({view: 'busca', pilha: []})

    const [resultados, setResultados] = useState<AtaResumo[]>([])
    const [origem, setOrigem] = useState<string>('cache')
    const [termo, setTermo] = useState('')
    const [buscando, setBuscando] = useState(false)
    const [erroBusca, setErroBusca] = useState<string | null>(null)
    const [sugestoes, setSugestoes] = useState<string[]>([])
    const [irps, setIrps] = useState<IrpResumo[]>([])
    const [ultimaBusca, setUltimaBusca] = useState<BuscaFormValues | null>(null)

    const [historico, setHistorico] = useState<JustificativaResumo[]>([])
    const [carregandoHist, setCarregandoHist] = useState(false)

    const [ata, setAta] = useState<AtaDetalhe | null>(null)
    const [resumoSel, setResumoSel] = useState<AtaResumo | null>(null)
    const [carregandoAta, setCarregandoAta] = useState(false)

    const [respostas, setRespostas] = useState<RespostasEntrevista>(defaultRespostas('M'))
    const [evidencias, setEvidencias] = useState<Record<string, string>>({})

    const [justificativa, setJustificativa] = useState<JustificativaOut | null>(null)
    const [gerando, setGerando] = useState(false)
    const [autoIa, setAutoIa] = useState(false) // IA automática só em geração nova (não no histórico)

    const [sugerindo, setSugerindo] = useState(false)

    // Notifier (padrão da casa): em vez de polling, o backend avisa por websocket
    // quando a justificativa conclui. Aqui consumimos a mensagem do módulo e
    // guardamos qual id estamos aguardando (pendenteRef).
    const {caronaSocketMessage} = useContext(HistoryContext)
    const pendenteRef = useRef<string | null>(null)
    // Correlação independente da sugestão de "demanda concreta" — ref e effect
    // próprios, sem cruzar com pendenteRef/justificativa_id acima.
    const pendenteSugestaoRef = useRef<string | null>(null)
    // Timer do poll de reconciliação da sugestão (setTimeout recursivo). Cancelado
    // quando o WS resolve primeiro, quando o teto é atingido, ou no unmount.
    const sugestaoPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Espelha `view` p/ o toast de erro da sugestão (que pode chegar bem depois, via
    // WS ou fallback) checar a tela ATUAL, não a capturada no momento do clique.
    const viewRef = useRef(view)
    viewRef.current = view

    // Nome do órgão aderente (para o cabeçalho do documento) — vem do JWT do core.
    const aderenteNome = useMemo(() => {
        try {
            const auth = new CookiesService().getAuthData()
            const dec: any = auth?.access_token ? decodeToken(auth.access_token) : null
            return dec?.organization?.name || 'Órgão aderente'
        } catch {
            return 'Órgão aderente'
        }
    }, [])

    // Recebeu aviso do notifier (websocket) de que a justificativa que estávamos
    // aguardando concluiu → busca o resultado final e atualiza a tela. Substitui o
    // polling: nada de ficar perguntando "já terminou?" ao backend.
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.justificativa_id || msg.project_id
        if (!id || id !== pendenteRef.current) return
        if (msg.status && msg.status !== 'PROCESSANDO') {
            pendenteRef.current = null
            setGerando(false)
            caronaApi
                .obterJustificativa(id)
                .then(setJustificativa)
                .catch(() => {})
        }
    }, [caronaSocketMessage])

    // Cancela o poll de reconciliação da sugestão em andamento, se houver. Chamado
    // pelo próprio poll ao terminar, por finalizarSugestao (quando o WS resolve
    // primeiro) e no unmount — seguro de chamar mais de uma vez (clearTimeout num id
    // já nulo/disparado é no-op).
    function pararPollSugestao() {
        if (sugestaoPollTimerRef.current !== null) {
            clearTimeout(sugestaoPollTimerRef.current)
            sugestaoPollTimerRef.current = null
        }
    }

    // Aplica o resultado terminal (APTA/ERRO) da sugestão de demanda, vindo por
    // qualquer um dos dois caminhos (WS + GET de confirmação, ou o poll de
    // reconciliação). Só aqui o botão volta a ficar habilitado — nunca logo após o
    // POST resolver.
    function finalizarSugestao(s: SugestaoDemandaOut) {
        pararPollSugestao()
        pendenteSugestaoRef.current = null
        setSugerindo(false)
        if (s.status === 'APTA') {
            setRespostas((prev) => ({...prev, demanda_descricao: s.texto || ''}))
        } else if (s.status === 'ERRO' && viewRef.current === 'entrevista') {
            // Se o usuário já saiu da tela de entrevista, reseta em silêncio (sem toast).
            toast.error('Não foi possível sugerir a demanda com a IA.')
        }
    }

    // Sugestão de "demanda concreta" (independente da justificativa acima): ref e
    // effect próprios. Também cai em `project_id` (como no effect da justificativa
    // acima) porque o notifier valida a mensagem contra schemas fechados que não
    // declaram `sugestao_demanda_id` e descartam o campo em silêncio. Isso não
    // colide com o fluxo de justificativa: a correlação é por VALOR contra a ref
    // própria de cada fluxo (pendenteSugestaoRef aqui, pendenteRef lá), não por
    // nome de campo/discriminador.
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.sugestao_demanda_id || msg.project_id
        if (!id || id !== pendenteSugestaoRef.current) return
        if (msg.status && msg.status !== 'PROCESSANDO') {
            caronaApi
                .obterSugestaoDemanda(id)
                .then((s) => {
                    if (pendenteSugestaoRef.current === id && s.status !== 'PROCESSANDO') {
                        finalizarSugestao(s)
                    }
                })
                .catch(() => {
                    /* best-effort: o poll de reconciliação tenta de novo na próxima rodada */
                })
        }
    }, [caronaSocketMessage])

    // Garante que o poll de reconciliação da sugestão não continue rodando (e
    // tentando setState) depois que o componente desmontar.
    useEffect(() => {
        return () => pararPollSugestao()
    }, [])

    async function handleBuscar(form: BuscaFormValues) {
        setTermo(form.objeto)
        setUltimaBusca(form)
        setSugestoes([])
        setIrps([])
        setBuscando(true)
        setErroBusca(null)
        navegar('resultados')
        // IRP em paralelo (rápido, do nosso banco): trilho "Participar". Best-effort,
        // não bloqueia as atas (PNCP ao vivo). Aparece como seção própria na lista.
        caronaApi.buscarIrp(form.objeto, form.uf || undefined).then(setIrps).catch(() => setIrps([]))
        try {
            // Busca AMPLA: sem esfera_aderente (não aplica vedação). Inclui as atas
            // "adesão permitida" (verde) E "condicional / a confirmar" (amarela) —
            // o PNCP raramente confirma a permissão; o gestor confirma na entrevista.
            // Exclui só as vedadas e as vencidas.
            const data = await caronaApi.buscar({
                objeto: form.objeto,
                esfera_aderente: null,
                uf: form.uf || null,
                apenas_vigentes: form.vigentes,
                apenas_permitidas: false,
                incluir_vedadas: false,
                limite: 100,
            })
            let res = data.resultados || []
            if (form.esferaGerenciador) {
                res = res.filter((a) => a.esfera_codigo === form.esferaGerenciador)
            }
            setResultados(res)
            setOrigem(data.origem || 'cache')
            // 0 resultados → pede sugestões de termo (IA via n8n), best-effort.
            if (res.length === 0) {
                caronaApi.sugestoes(form.objeto).then(setSugestoes).catch(() => setSugestoes([]))
            }
        } catch (e: any) {
            const status = e?.response?.status
            setErroBusca(
                status === 401 || status === 403
                    ? 'Não autorizado pela API (verifique o token/DEV_AUTH_KEY do carona-service).'
                    : `Não foi possível falar com a API da Ata Inteligente (${e?.message || 'erro'}).`
            )
            setResultados([])
        } finally {
            setBuscando(false)
        }
    }

    async function abrirDetalhe(numeroControle: string) {
        // Navegação otimista: mostra o resumo (já conhecido) na hora e carrega os
        // itens/saldo do PNCP dentro da própria página de detalhe.
        const resumo = resultados.find((a) => a.numero_controle_pncp === numeroControle) || null
        setResumoSel(resumo)
        setCarregandoAta(true)
        setAta(null)
        navegar('detalhe')
        try {
            const data = await caronaApi.detalhe(numeroControle)
            setAta(data)
        } catch (e: any) {
            toast.error(`Falha ao carregar o detalhe da ata: ${e?.message || 'erro'}`)
            voltar()
        } finally {
            setCarregandoAta(false)
        }
    }

    async function iniciarEntrevista(numeroControle: string) {
        let a = ata
        if (!a || a.numero_controle_pncp !== numeroControle) {
            setCarregandoAta(true)
            try {
                a = await caronaApi.detalhe(numeroControle)
                setAta(a)
            } catch (e: any) {
                toast.error(`Falha ao carregar a ata: ${e?.message || 'erro'}`)
                setCarregandoAta(false)
                return
            }
            setCarregandoAta(false)
        }
        // mantém a esfera escolhida antes (ou default), o gestor confirma na entrevista
        setRespostas((r) => ({...defaultRespostas(r.esfera_aderente)}))
        setEvidencias({})
        navegar('entrevista')
    }

    async function gerarJustificativa() {
        if (!ata) return
        const evidenciasList: EvidenciaIn[] = Object.entries(evidencias)
            .filter(([, ref]) => (ref || '').trim())
            .map(([requisito_id, ref]) => ({requisito_id, ref: ref.trim()}))

        const req: CriarJustificativaRequest = {
            ata_numero_controle: ata.numero_controle_pncp,
            modo: 'v1',
            respostas: {...respostas} as unknown as Record<string, unknown>,
            evidencias: evidenciasList,
        }

        // Navegação otimista: entra já na página da justificativa (com dicas) e
        // a verificação de conformidade é carregada ali, em tempo real.
        setJustificativa(null)
        setAutoIa(true) // geração nova → a IA reescreve a 1ª versão automaticamente
        setGerando(true)
        navegar('justificativa')
        try {
            const criada = await caronaApi.criarJustificativa(req)
            setJustificativa(criada)
            if (criada.status === 'PROCESSANDO') {
                // Assíncrono (modo completo): o resultado chega pelo NOTIFIER (websocket),
                // tratado no useEffect acima. Sem polling. Rede de segurança única: se em
                // ~8s nada chegou (ex.: socket indisponível), reconcilia 1x via GET.
                pendenteRef.current = criada.id
                setTimeout(async () => {
                    if (pendenteRef.current !== criada.id) return
                    try {
                        const j = await caronaApi.obterJustificativa(criada.id)
                        if (pendenteRef.current === criada.id && j.status !== 'PROCESSANDO') {
                            pendenteRef.current = null
                            setJustificativa(j)
                        }
                    } catch {
                        /* best-effort */
                    }
                }, 8000)
            } else {
                // v1 (determinístico): a justificativa já vem pronta na resposta.
                pendenteRef.current = null
            }
        } catch (e: any) {
            const status = e?.response?.status
            toast.error(
                status === 406
                    ? 'Sem créditos de documento para gerar a justificativa.'
                    : `Falha ao gerar a justificativa (${e?.message || 'erro'}).`
            )
            voltar() // sem documento: volta para a tela anterior
        } finally {
            setGerando(false)
        }
    }

    // Agenda a próxima rodada do poll de reconciliação da sugestão: reconsulta o
    // status a cada SUGESTAO_POLL_INTERVAL_MS até resolver (finalizarSugestao) ou até
    // estourar o teto de SUGESTAO_POLL_TIMEOUT_MS, quando desiste — estado terminal
    // próprio, sem passar por finalizarSugestao (um timeout não é um status real do
    // backend).
    function agendarPollSugestao(id: string, prazoFinal: number) {
        sugestaoPollTimerRef.current = setTimeout(async () => {
            if (pendenteSugestaoRef.current !== id) return
            try {
                const s = await caronaApi.obterSugestaoDemanda(id)
                if (pendenteSugestaoRef.current !== id) return
                if (s.status !== 'PROCESSANDO') {
                    finalizarSugestao(s)
                    return
                }
            } catch {
                /* best-effort: a próxima rodada do poll tenta de novo */
            }
            // Reconfirma após o catch: o await acima pode ter rejeitado bem depois de
            // o outro caminho (WS) já ter resolvido esta mesma sugestão — sem isso, essa
            // rodada "zumbi" poderia desistir (e até mostrar o toast de teto) por cima
            // de um fluxo que já terminou.
            if (pendenteSugestaoRef.current !== id) return
            if (Date.now() >= prazoFinal) {
                pararPollSugestao()
                pendenteSugestaoRef.current = null
                setSugerindo(false)
                if (viewRef.current === 'entrevista') {
                    toast.error('A sugestão está demorando mais que o esperado. Tente novamente.')
                }
                return
            }
            agendarPollSugestao(id, prazoFinal)
        }, SUGESTAO_POLL_INTERVAL_MS)
    }

    async function sugerirDemanda() {
        if (!ata) return
        // Baseia-se no ITEM pesquisado + no ENTE aderente — não no objeto/contexto da
        // ata (a ata pode ser de outra finalidade; o item é o que o aderente quer).
        const item = (termo || '').trim() || ata.objeto
        setSugerindo(true)
        try {
            const sug = await caronaApi.sugerirDemanda(item, ESFERA_LABEL[respostas.esfera_aderente] || '', aderenteNome)
            // Assíncrono: o resultado chega pelo NOTIFIER (websocket), tratado no
            // useEffect acima. Rede de segurança: poll de reconciliação via GET a cada
            // SUGESTAO_POLL_INTERVAL_MS (o backend real leva ~14-17s, bem além de um
            // único check) até SUGESTAO_POLL_TIMEOUT_MS, para o caso do socket não
            // entregar (ou entregar tarde demais).
            pendenteSugestaoRef.current = sug.id
            agendarPollSugestao(sug.id, Date.now() + SUGESTAO_POLL_TIMEOUT_MS)
        } catch (err: any) {
            setSugerindo(false)
            toast.error(`Não foi possível sugerir (${err?.message || 'erro'}).`)
        }
    }

    async function abrirHistorico() {
        navegar('historico')
        setCarregandoHist(true)
        try {
            setHistorico(await caronaApi.listarJustificativas())
        } catch (e: any) {
            toast.error(`Falha ao carregar o histórico (${e?.message || 'erro'}).`)
        } finally {
            setCarregandoHist(false)
        }
    }

    async function reabrir(id: string) {
        try {
            const j = await caronaApi.obterJustificativa(id)
            const estrut = (j.documento_estruturado || {}) as any
            setAutoIa(false) // reabrir do histórico → não re-roda a IA automaticamente
            setRespostas({...defaultRespostas('M'), ...(estrut.respostas || {})})
            setEvidencias({})
            setJustificativa(j)
            try {
                setAta(await caronaApi.detalhe(j.ata_numero_controle))
            } catch {
                setAta(null)
            }
            navegar('justificativa')
        } catch (e: any) {
            toast.error(`Falha ao abrir a justificativa (${e?.message || 'erro'}).`)
        }
    }

    function onSugestao(t: string) {
        const base = ultimaBusca || {objeto: t, esferaGerenciador: '', uf: '', vigentes: true}
        handleBuscar({...base, objeto: t})
    }

    function renderView() {
        if (view === 'busca') return <BuscaForm onBuscar={handleBuscar} onHistorico={abrirHistorico} />
        if (view === 'historico') {
            return (
                <HistoricoView
                    justificativas={historico} loading={carregandoHist}
                    onAbrir={reabrir} onVoltar={voltar}
                />
            )
        }
        if (view === 'resultados') {
            return (
                <ResultadosList
                    resultados={resultados} origem={origem} termo={termo}
                    loading={buscando} erro={erroBusca} sugestoes={sugestoes} irps={irps}
                    onRefinar={voltar} onSugestao={onSugestao}
                    onAbrir={abrirDetalhe} onAvaliar={iniciarEntrevista}
                />
            )
        }
        if (view === 'detalhe') {
            return (
                <DetalheAta
                    ata={ata} resumo={resumoSel}
                    onVoltar={voltar}
                    onAvaliar={() => {
                        const nc = ata?.numero_controle_pncp || resumoSel?.numero_controle_pncp
                        if (nc) iniciarEntrevista(nc)
                    }}
                />
            )
        }
        if (view === 'entrevista' && ata) {
            return (
                <Entrevista
                    ata={ata} termoBuscado={termo} aderenteNome={aderenteNome}
                    respostas={respostas} setRespostas={setRespostas}
                    evidencias={evidencias} setEvidencias={setEvidencias}
                    gerando={gerando} onGerar={gerarJustificativa}
                    sugerindo={sugerindo} onSugerirDemanda={sugerirDemanda}
                    onVoltar={voltar}
                />
            )
        }
        if (view === 'justificativa') {
            if (justificativa) {
                return (
                    <JustificativaView
                        justificativa={justificativa} ata={ata}
                        respostas={respostas} evidencias={evidencias} aderenteNome={aderenteNome}
                        autoIa={autoIa}
                        onVoltarEntrevista={voltar}
                        onNovaBusca={irParaBusca}
                    />
                )
            }
            // Otimista: já estamos na página enquanto a justificativa é gerada.
            return (
                <div>
                    <header className="page-header">
                        <div className="crumb">
                            <a onClick={irParaBusca}>Ata Inteligente</a>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <a onClick={voltar}>Entrevista</a>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span>Gerando…</span>
                        </div>
                        <div className="header-row">
                            <h1 className="lg-h2">Justificativa de adesão</h1>
                        </div>
                    </header>
                    <div className="page-content">
                        <DicasLoading titulo="Gerando a justificativa e verificando a conformidade…" />
                    </div>
                </div>
            )
        }
        return <BuscaForm onBuscar={handleBuscar} onHistorico={abrirHistorico} />
    }

    return (
        <main
            className="carona-content cscope"
            style={{'--brand-blue': COLORS.primariaEscura} as CSSProperties}
        >
            {renderView()}
        </main>
    )
}
